import React, { useCallback, useId } from 'react';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import { useDispatch } from 'react-redux';

import TriagedMetric from 'src/screen/v2/Protected/DashboardScreen/TriagedAlarms/TriagedMetric';
import { Button } from 'src/component/ds/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/component/ds/ui/card';
import { MetricsSkeleton, TotalSkeleton } from 'src/screen/v2/Protected/DashboardScreen/TriagedAlarms/TriagedAlarmsSkeleton';
import { TooltipProvider } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';
import { pushHistory } from 'src/module/navigation/action';
import { useTranslation } from 'src/i18n';


const ALARM_HISTORY_PATH = '/alarms/history';
const WORK_TICKETS_PATH = '/notifications';

/*
 * TODO: point this at a combined shelved-alarms view once the redesign adds
 * one. An alarm is shelved by a loto OR a nuisance note, but those are two
 * separate v1 screens (/notes/loto, /notes/nuisance) with no combined list, so
 * this link shows only the loto half -- the nuisance-shelved alarms counted in
 * the metric beside it are not reachable from here.
 */
const SHELVED_PATH = '/notes/loto';


const WORK_ORDER_ICON = AssignmentOutlined;
const SHELVED_ICON = Inventory2Outlined;

export interface TriagedAlarmsProps {
  total?: number;
  resolvedThisWeek?: number;
  withWorkOrders?: number;
  closedThisWeek?: number;
  shelved?: number;
  expiringSoon?: number;
  workOrderGroups?: number[];
  loading?: boolean;
  className?: string;
}

/**
 * The dashboard's Triaged Alarms card: live alarms somebody has already
 * actioned -- given a work order, or shelved with a LOTO/nuisance note -- plus
 * what is about to lapse. The counterpart to the Alarms to Triage card above
 * it, which lists the live alarms with none of that yet.
 *
 * Skeletons while the counts are being fetched, otherwise the full card --
 * including at zero, where every sub-metric and link stays in place reading 0.
 *
 * @param {TriagedAlarmsProps} props see TriagedAlarmsProps
 * @returns {JSX.Element} the card
 */
export default function TriagedAlarms ({
  total = 0,
  resolvedThisWeek = 0,
  withWorkOrders = 0,
  closedThisWeek = 0,
  shelved = 0,
  expiringSoon = 0,
  workOrderGroups = [],
  loading = false,
  className
}: TriagedAlarmsProps) {
  const { t, tPlural } = useTranslation();
  const dispatch = useDispatch();

  const openPath = useCallback((path: string) => () => {
    dispatch(pushHistory(path));
  }, [dispatch]);


  const workOrderHint = workOrderGroups.length
    ? `${t('dashboard.workOrderAlarms')} ${t('dashboard.workOrderAlarmsBars', { count: workOrderGroups.length })}`
    : t('dashboard.workOrderAlarms');


  const titleId = useId();

  return (
    <Card className={cn('ds-shadow-ds-sm', className)} data-testid='triaged-alarms'>

      <CardHeader className='ds-gap-1 ds-p-8 ds-pb-4'>
        <CardTitle id={titleId} className='ds-text-ds-sm ds-font-normal ds-text-muted-foreground'>
          {t('dashboard.triagedAlarms')}
        </CardTitle>

        <div className='ds-flex ds-items-center ds-gap-3'>
          {loading
            ? <TotalSkeleton />
            : (
              <p
                className='ds-font-heading ds-text-ds-h1 ds-font-medium ds-tracking-[-1.5px] ds-text-foreground'
                data-testid='triaged-alarms-total'
                aria-describedby={titleId}
              >
                {total}
              </p>
            )}

          {!loading && (
            <Button
              variant='secondary'
              size='xs'
              onClick={openPath(ALARM_HISTORY_PATH)}
              data-testid='triaged-alarms-resolved'
            >
              <span>{tPlural('dashboard.alarmsResolvedThisWeek', resolvedThisWeek)}</span>
              <ChevronRightIcon fontSize='inherit' aria-hidden='true' />
            </Button>
          )}
        </div>
      </CardHeader>

      {loading ? (
        <CardContent className='ds-px-8 ds-pb-8 ds-pt-0'><MetricsSkeleton /></CardContent>
      ) : (
        <CardContent className='ds-px-8 ds-pb-8 ds-pt-0'>

          <TooltipProvider delayDuration={200}>
            <div className='ds-grid ds-grid-cols-1 ds-gap-8 ds-narrow:ds-grid-cols-2'>
              <TriagedMetric
                icon={WORK_ORDER_ICON}
                label={tPlural('dashboard.alarmsWithWorkOrders', withWorkOrders)}
                linkLabel={tPlural('dashboard.workOrdersClosedThisWeek', closedThisWeek)}
                onLinkClick={openPath(WORK_TICKETS_PATH)}
                segments={workOrderGroups}
                labelHint={workOrderHint}
                testId='triaged-alarms-work-orders'
              />
              <TriagedMetric
                icon={SHELVED_ICON}
                label={tPlural('dashboard.alarmsShelved', shelved)}
                linkLabel={tPlural('dashboard.notesExpireSoon', expiringSoon)}
                onLinkClick={openPath(SHELVED_PATH)}
                segments={shelved ? [1] : []}
                labelHint={t('dashboard.shelvedAlarms')}
                testId='triaged-alarms-shelved'
              />
            </div>
          </TooltipProvider>
        </CardContent>
      )}
    </Card>
  );
}
